import { t } from '@lingui/macro';

import { ApiKeyResource, ApiKeyRotatedFrom, ApiKeyServerStatus } from '@api/v4/organizations/api_keys';

import { ApiKeyRow, ApiKeyStatus } from './types';

/** Maps the server status enum onto the three chip states used by the UI. */
export const mapApiKeyStatus = (status: ApiKeyServerStatus): ApiKeyStatus => {
  switch (status) {
    case 'expiring_soon':
      return 'expiring';
    case 'deactivated':
    case 'revoked':
      return 'disabled';
    case 'active':
    default:
      return 'active';
  }
};

/**
 * The "New" badge marks a freshly-rotated successor whose predecessor is still
 * usable during the rotation grace period. Both token lines keep showing while a
 * predecessor exists, but the badge drops once the predecessor is deactivated or
 * revoked. Older payloads omit `status` — treat a missing status as still-usable
 * for backward compatibility.
 */
export const showNewBadgeFor = (rotatedFrom: ApiKeyRotatedFrom | null): boolean => {
  if (!rotatedFrom) return false;
  return rotatedFrom.status !== 'deactivated' && rotatedFrom.status !== 'revoked';
};

/** Maps an api_key JSON:API resource from the server onto the table row shape. */
export const mapApiKey = ({ attributes, id }: ApiKeyResource): ApiKeyRow => ({
  createdAt: attributes.created_at,
  createdBy: attributes.created_by_user?.name ?? '',
  description: attributes.description ?? '',
  expiresAt: attributes.expires_at,
  id,
  name: attributes.name,
  rotatedFromTokenPrefix: attributes.rotated_from?.token_prefix ?? null,
  showNewBadge: showNewBadgeFor(attributes.rotated_from),
  source: attributes.source,
  status: mapApiKeyStatus(attributes.status),
  tokenPrefix: attributes.token_prefix,
});

export const statusLabel = (status: ApiKeyStatus): string => {
  switch (status) {
    case 'expiring':
      return t`Expiring Soon`;
    case 'disabled':
      return t`Deactivated`;
    case 'active':
    default:
      return t`Active`;
  }
};

/** Formats an ISO timestamp as MM/DD/YYYY. Returns empty string for null/invalid dates. */
export const formatDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const day = `${date.getDate()}`.padStart(2, '0');
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};
